/**
 * tutorSessionAdapter — les lives du masteur (`currentUser.live_sessions` d'un tuteur, lu par
 * `selectAccount`) → la section « Aujourd'hui » de « Ma journée », la boîte qu'une séance touchée
 * ouvre, et la pastille de l'onglet Accueil.
 *
 * API-aware (`UserResource` branche tuteur → `V1\LiveSessionResource`, servie séance par séance :
 * `next_occurrences` ne porte que les dates qu'il tient). Jamais importé par le DS.
 *
 * Ports littéraux de symboles qui vivent dans `src/tutor/**`, `src/student/**` et `src/common/**` —
 * interdits à `ports/**` (`src/ports/CLAUDE.md` § Dependency rules) — recopiés, avec leur fichier :
 *
 * - `findClosestNextOccurrence` — `student/account/onboarding/LiveRoomCard.js`
 * - `sortLiveSessionsCallback` — `data/utility/dataUtils.js` (il importe le précédent, d'où la copie)
 * - la coupe à trois de `TutorNextEventsCard` — `tutor/home/interview/TutorNextEventsCard.js`
 * - la liste des élèves — `tutor/lives/StudentsListStringForOccurrence.js`
 * - les libellés de `SessionTypeBadgeM` — `getLiveSessionTypeMeta`, `data/utility/liveSessionType.js`
 *   (le fichier importe le thème legacy, d'où la copie des seuls libellés)
 * - le bouton « GÉRER LES RDV » et ses boîtes — `renderButton`, `tutor/lives/TutorLiveCardComponent.js`
 * - la pastille de l'Accueil — `homeNotifications`, `tutor/tutorTabScreens.js`
 *
 * Tant que l'ancien accueil vit derrière son switch, ces symboles restent une seconde copie qui
 * peut diverger : une évolution d'une règle se reporte ici.
 */
import moment from "moment"

import { hasFeature } from "../../../../../data/utility/Utils"
import { type TutorSessionNotice } from "../../../../../design-system/templates/TutorSessionNoticeDialog"
import { joinFrench } from "../../../ui/formatters/textFormatter"
import { type UILiveSessionStatus, type UISubject } from "../../../ui/types"
import {
    type UITutorSession,
    type UITutorSessionStudent,
    type UITutorSessionStudentMark,
} from "../../../ui/types/tutorCoquilleAccueil"
import {
    toUITutor,
    type ApiAttendanceFlag,
    type ApiLiveOccurrenceTutor,
} from "../../parent/LiveSessions/liveSessionListAdapter"
import { indexSubjectsById, toUISubject, type ApiSchoolSubject } from "../../parent/shared/schoolSubjectAdapter"

// ── Formes du serveur ───────────────────────────────────────────────────────

/** Un élève ajouté ou retiré pour une date (`LiveSession::getTemporaryLiveAssignmentsMap`). */
export type ApiTemporaryUser = {
    id?: string | number
    display_name?: string
    class?: string | null
}

/** Une ligne de `next_occurrences` (`LiveSession::getLocalNextOccurrences`), date « YYYY-MM-DD HH:mm ». */
export type ApiTutorNextOccurrence = {
    date?: string
    live_session_id?: string | number
    substitution_id?: string | number | null
    substitute_id?: string | number | null
    rescheduling_id?: string | number | null
    finalized_at?: string | null
    added_users?: ApiTemporaryUser[] | null
    removed_users?: ApiTemporaryUser[] | null
}

/**
 * Un élève du roster. Le masteur est un lecteur du staff (`User::receivesWhole`) : il reçoit
 * `UserLighterResource`, avec le profil, le paiement et le pivot du siège (`users_live_sessions`).
 */
export type ApiTutorSessionStudent = {
    id?: string | number
    firstname?: string
    lastname?: string
    display_name?: string
    class?: string | null
    profile?: { class?: string | null } | null
    is_payment_blocked?: boolean
    is_tutor?: boolean
    pivot?: { school_subject_id?: string | number | null; pedagogical_objective_id?: string | number | null } | null
}

/** Une réservation en attente — lue par le legacy, que `LiveSessionResource` ne sert pas aujourd'hui. */
export type ApiTutorReservation = { mode?: string | null; user?: ApiTutorSessionStudent | null }

/** Une séance du masteur (`V1\LiveSessionResource`). */
export type ApiTutorLiveSession = {
    id?: string | number
    duration?: number | null
    live_room_id?: string | number | null
    live_room?: { id?: string | number; type?: string | null; version_ft?: string[] | string | null } | null
    tutor?: ApiLiveOccurrenceTutor | null
    /** L'école, servie seulement au masteur qui travaille dans plusieurs (`spaceLabelFor`). */
    space_label?: string | null
    /** Le retard signalé, en minutes, pour la séance du jour. */
    delay_today?: number | null
    students?: ApiTutorSessionStudent[] | null
    users?: ApiTutorSessionStudent[] | null
    reservations?: ApiTutorReservation[] | null
    attendance_flags?: ApiAttendanceFlag[] | null
    next_occurrences?: ApiTutorNextOccurrence[] | null
}

// ── La forme de l'accueil ───────────────────────────────────────────────────

/**
 * Une séance de « Ma journée » : `UITutorSession`, plus ce que les gestes de sa boîte lisent.
 * Rien ici ne s'affiche sur la ligne.
 */
export type UITutorHomeSession = UITutorSession & {
    // ── Action metadata (la boîte de la séance ; pas rendu par la ligne) ──
    /** « YYYY-MM-DD HH:mm », la date locale : `occurrence_date` de `cancelTutorDelay`. */
    occurrenceDate: string
    /** Le retard signalé, en minutes : la boîte `late` et son « Annuler la demande ». */
    lateMinutes?: number
    /** Le remplacement demandé pour cette date : `{ id }` de `cancelSubstitution`. */
    substitutionId?: string | number
    /** Un masteur a accepté le remplacement : la boîte n'annonce plus qu'il est accepté. */
    substitutionAccepted?: boolean
}

export type TutorHomeSessions = {
    /** Les lives du jour, dans l'ordre de l'ancienne carte : le premier en grand. */
    today: UITutorHomeSession[]
    /** Un jour sans live : les trois prochains. Vide un jour de live. */
    upcoming: UITutorHomeSession[]
}

type Now = moment.Moment | Date | string | number

// ── Ports littéraux ─────────────────────────────────────────────────────────

/** Les lives qu'un jour sans live montre — `tab = data?.slice(0, 3)` de `TutorNextEventsCard`. */
export const TUTOR_HOME_UPCOMING_MAX = 3

/** Les deux côtés écrivent « YYYY-MM-DD HH:mm » : normalisé pour qu'une jointure ne dérive pas. */
function dateKey(date: string | null | undefined): string | null {
    if (!date) return null
    const parsed = moment(date)
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : null
}

/**
 * La séance qu'une carte montre — `findClosestNextOccurrence([liveSession])` : la plus proche des
 * occurrences non finalisées, sans filtre sur le jour (une occurrence d'hier restée dans des données
 * périmées est prise, comme dans le legacy).
 */
function closestNextOccurrence(session: ApiTutorLiveSession): ApiTutorNextOccurrence | undefined {
    return [...(session.next_occurrences ?? [])]
        .filter((occurrence) => !occurrence?.finalized_at)
        .sort((a, b) => moment(a?.date).diff(b?.date))[0]
}

/**
 * La clé de tri de `sortLiveSessionsCallback` : l'écart à maintenant en minutes, en valeur absolue
 * le jour même — un live commencé il y a une demi-heure passe avant celui de dans deux heures, et
 * celui de ce matin, resté ouvert, après eux.
 */
function sortKey(date: string, now: moment.Moment): number {
    const live = moment(date)
    const diff = live.diff(now, "minutes")
    return live.isSame(now, "day") ? Math.abs(diff) : diff
}

/** Les libellés de `getLiveSessionTypeMeta` (`SessionTypeBadgeM`). */
function kindLabelOf(type: string | null | undefined): string {
    switch (type) {
        case "individual":
            return "Individuel"
        case "biweekly":
            return "Cours partagé"
        case "study_group":
            return "Étude encadrée"
        case "trial":
            return "Essai"
        case "holiday_course":
            return "Stage"
        case "support":
            return "Support"
        case "custom":
            return "Tutoriel"
        case "holiday_workbook":
            return "Cours d'été"
        default:
            return "Autre"
    }
}

type RosterEntry = {
    student: ApiTutorSessionStudent
    marks: UITutorSessionStudentMark[]
}

/**
 * Les élèves de cette date, dans l'ordre de `StudentsListStringForOccurrence` : ajoutés pour cette
 * date, retirés pour cette date, retirés pour une AUTRE date (sans marque), le roster (moins tous
 * ceux-là, et moins quiconque est ajouté à une autre date), puis les réservations ; dédoublonnés,
 * le premier l'emporte. Un ajouté ou un retiré sans classe est écarté, comme dans le legacy.
 */
function rosterOf(session: ApiTutorLiveSession, occurrence: ApiTutorNextOccurrence): RosterEntry[] {
    const withClass = (users: ApiTemporaryUser[] | null | undefined) => (users ?? []).filter((user) => !!user?.class)
    const sameId = (a: { id?: string | number }, b: { id?: string | number }) => String(a.id) === String(b.id)

    const occurrences = session.next_occurrences ?? []
    const allAdded = occurrences.flatMap((occ) => withClass(occ?.added_users))
    const allRemoved = occurrences.flatMap((occ) => withClass(occ?.removed_users))
    const added = withClass(occurrence.added_users)
    const removed = withClass(occurrence.removed_users)
    const removedElsewhere = allRemoved.filter(
        (user) => !(occurrence.removed_users ?? []).some((removedUser) => sameId(removedUser, user))
    )

    const students = session.students ?? session.users?.filter((user) => !user?.is_tutor) ?? []
    const regulars = students.filter(
        (user) =>
            !added.some((other) => sameId(other, user)) &&
            !removed.some((other) => sameId(other, user)) &&
            !removedElsewhere.some((other) => sameId(other, user)) &&
            !allAdded.some((other) => sameId(other, user)) &&
            !user?.is_tutor
    )
    const reservations = (session.reservations ?? [])
        .filter((reservation) => reservation?.user != null)
        .map((reservation): RosterEntry => ({
            student: reservation.user as ApiTutorSessionStudent,
            marks: [reservation.mode === "adhoc" ? "reservation_adhoc" : "reservation"],
        }))

    const entries: RosterEntry[] = [
        ...added.map((student): RosterEntry => ({ student, marks: ["added"] })),
        ...removed.map((student): RosterEntry => ({ student, marks: ["removed"] })),
        ...removedElsewhere.map((student): RosterEntry => ({ student, marks: [] })),
        ...regulars.map((student): RosterEntry => ({ student, marks: [] })),
        ...reservations,
    ]
    return entries.filter(
        (entry, index, self) =>
            entry.student?.id != null && index === self.findIndex((other) => sameId(other.student, entry.student))
    )
}

/**
 * Un élève de la ligne. Le nom est `display_name` (`forceDisplayName`), la classe celle du profil.
 * La matière n'existait pas sur l'ancienne carte : c'est celle de son siège (`pivot.school_subject_id`),
 * lue sur le roster même pour un ajouté ou un retiré, qui arrive sans elle.
 *
 * Les marques se cumulent : le DS choisit laquelle colore le suffixe (impayé, puis retard, puis
 * absence — l'ordre du legacy), comme l'ancienne ligne qui posait le cadenas et l'horloge ensemble.
 */
function toStudent(
    entry: RosterEntry,
    session: ApiTutorLiveSession,
    occurrenceKey: string,
    subjectsById: Record<string, ApiSchoolSubject>
): UITutorSessionStudent {
    const { student } = entry
    const onRoster = (session.students ?? session.users ?? []).find((user) => String(user?.id) === String(student.id))
    const subjectId = student.pivot?.school_subject_id ?? onRoster?.pivot?.school_subject_id
    const subject = subjectId != null ? subjectsById[String(subjectId)] : undefined

    const marks = [...entry.marks]
    const paymentBlocked = student.is_payment_blocked ?? onRoster?.is_payment_blocked
    if (paymentBlocked) marks.push("payment_blocked")
    // `find` : le premier drapeau du jour l'emporte, comme dans le legacy.
    const flag = (session.attendance_flags ?? []).find(
        (candidate) => String(candidate?.user_id) === String(student.id) && dateKey(candidate?.date) === occurrenceKey
    )
    if (flag?.type) marks.push(flag.type === "late" ? "late" : "absent")

    const className = student.profile?.class ?? student.class ?? undefined
    return {
        id: student.id as string | number,
        name: student.display_name ?? [student.firstname, student.lastname].filter(Boolean).join(" "),
        ...(className ? { className } : {}),
        ...(subject != null ? { subjects: [toUISubject(subject)] } : {}),
        ...(marks.length > 0 ? { marks } : {}),
    }
}

/** Les matières de la séance : celles de ses élèves, sans doublon, dans leur ordre. */
function sessionSubjects(students: UITutorSessionStudent[]): UISubject[] {
    const seen = new Set<string>()
    return students
        .flatMap((student) => student.subjects ?? [])
        .filter((subject) => {
            const key = String(subject.id)
            if (seen.has(key)) return false
            seen.add(key)
            return true
        })
}

/** « Jules et Léa » : les prénoms de ceux qui viennent — ni les retirés de la date, ni les réservations. */
function studentsLabelOf(students: UITutorSessionStudent[]): string | undefined {
    const firstnames = students
        .filter((student) => !student.marks?.some((mark) => mark === "removed" || mark.startsWith("reservation")))
        .map((student) => student.name.trim().split(/\s+/)[0] ?? "")
        .filter((name) => name !== "")
    return firstnames.length > 0 ? joinFrench(firstnames) : undefined
}

/**
 * Le stage dont le champion démarre la séance (`getPrepareLiveTask` : salle `holiday_course` au drapeau
 * `checkpoint-sb`, live d'aujourd'hui ou de demain) — le seul que le masteur prépare. La tâche ne
 * dit pas QUELS lives : la règle se relit ici, sur la séance.
 */
function isPreparable(session: ApiTutorLiveSession, date: string, now: moment.Moment): boolean {
    if (session.live_room?.type !== "holiday_course" || hasFeature(session.live_room, "checkpoint-sb") !== true) {
        return false
    }
    const day = moment(date)
    return day.isSame(now, "day") || day.isSame(moment(now).add(1, "day"), "day")
}

type Mapped = { session: UITutorHomeSession; key: number; isToday: boolean }

/** Une carte : la séance au jour de sa plus proche occurrence. Sans occurrence, inutilisable. */
function toTutorHomeSession(
    raw: ApiTutorLiveSession | null | undefined,
    subjectsById: Record<string, ApiSchoolSubject>,
    now: moment.Moment
): Mapped | null {
    if (raw?.id == null) return null
    const occurrence = closestNextOccurrence(raw)
    const occurrenceDate = dateKey(occurrence?.date)
    if (occurrence == null || occurrenceDate == null) return null

    const live = moment(occurrence.date)
    const isToday = live.isSame(now, "day")
    const isIndividual = raw.live_room?.type === "individual"
    const substitutionId = occurrence.substitution_id ?? undefined
    // `renderButton` ne pose aucun bouton pour un retard signalé sur un cours collectif d'un autre
    // jour sans remplacement (le live du jour est finalisé, la carte montre le suivant) : ni chip,
    // ni boîte. Toucher la ligne ouvre alors le déplacement — la ligne n'a pas d'état « inerte ».
    const lateApplies = !!raw.delay_today && (isIndividual || isToday || substitutionId != null)

    const students = rosterOf(raw, occurrence).map((entry) => toStudent(entry, raw, occurrenceDate, subjectsById))

    // Les états se cumulent : l'ancienne carte écrivait « * Replanifié » et « ** Remplacement » ensemble.
    const statuses: UILiveSessionStatus[] = []
    if (lateApplies) statuses.push("late")
    if (substitutionId != null) statuses.push("substitute")
    if (occurrence.rescheduling_id != null) statuses.push("rescheduled")

    const studentsLabel = studentsLabelOf(students)
    const spaceLabel = raw.space_label?.trim()
    const date = live.format("YYYY-MM-DDTHH:mm:ss")

    const session: UITutorHomeSession = {
        id: raw.id,
        date,
        duration: raw.duration ?? 0,
        tutor: toUITutor(raw.tutor),
        subjects: sessionSubjects(students),
        statuses,
        students,
        kindLabel: kindLabelOf(raw.live_room?.type),
        ...(studentsLabel != null ? { studentsLabel } : {}),
        ...(spaceLabel ? { spaceLabel } : {}),
        preparable: isPreparable(raw, date, now),

        liveSessionId: raw.id,
        liveRoomId: raw.live_room_id ?? raw.live_room?.id,
        roomType: raw.live_room?.type ?? undefined,
        occurrenceDate,
        ...(lateApplies ? { lateMinutes: Number(raw.delay_today) } : {}),
        ...(substitutionId != null ? { substitutionId, substitutionAccepted: occurrence.substitute_id != null } : {}),
    }
    return { session, key: sortKey(occurrence.date as string, now), isToday }
}

function mapAll(
    liveSessions: ReadonlyArray<ApiTutorLiveSession | null | undefined> | null | undefined,
    schoolSubjects: ReadonlyArray<ApiSchoolSubject | null | undefined> | null | undefined,
    now: Now
): Mapped[] {
    const at = moment(now)
    const subjectsById = indexSubjectsById(schoolSubjects as ApiSchoolSubject[] | null | undefined)
    return (liveSessions ?? [])
        .map((raw) => toTutorHomeSession(raw, subjectsById, at))
        .filter((mapped): mapped is Mapped => mapped != null)
        .sort((a, b) => a.key - b.key)
}

// ── Exports ─────────────────────────────────────────────────────────────────

/**
 * « Aujourd'hui » de « Ma journée » (l'ancienne « Mes prochains lives », `TutorNextLivesCard`) : une
 * séance par cours, au jour de sa plus proche occurrence, triée comme `sortLiveSessionsCallback`.
 * Un jour de live, tous les lives du jour ; un jour sans, les trois prochains, quel que soit leur jour
 * (l'ancienne carte en montrait trois dans tous les cas — audit « coquille-accueil », A1).
 *
 * `schoolSubjects` : `staticData.school_subjects`, pour la matière du siège de chaque élève.
 */
export function toTutorHomeSessions(
    liveSessions: ReadonlyArray<ApiTutorLiveSession | null | undefined> | null | undefined,
    schoolSubjects?: ReadonlyArray<ApiSchoolSubject | null | undefined> | null,
    now: Now = moment()
): TutorHomeSessions {
    const mapped = mapAll(liveSessions, schoolSubjects, now)
    const today = mapped.filter((entry) => entry.isToday).map((entry) => entry.session)
    if (today.length > 0) return { today, upcoming: [] }
    return { today, upcoming: mapped.slice(0, TUTOR_HOME_UPCOMING_MAX).map((entry) => entry.session) }
}

/**
 * La semaine du masteur (`tutor/lives`, l'ancienne modale « VOIR TOUS » de `TutorNextEventsCard`) :
 * les mêmes séances que l'accueil, toutes, sans la coupe à trois — la modale recevait la liste entière.
 */
export function toTutorWeekSessions(
    liveSessions: ReadonlyArray<ApiTutorLiveSession | null | undefined> | null | undefined,
    schoolSubjects?: ReadonlyArray<ApiSchoolSubject | null | undefined> | null,
    now: Now = moment()
): UITutorHomeSession[] {
    return mapAll(liveSessions, schoolSubjects, now).map((entry) => entry.session)
}

/**
 * La boîte qu'une séance touchée ouvre (`renderButton` de `TutorLiveCardComponent`) : le retard
 * d'abord, puis le remplacement. `null` : pas de boîte, l'écran ouvre le déplacement (`move-session`).
 *
 * Écart décidé (audit « coquille-accueil », « Gérer le rdv ») : un remplacement ACCEPTÉ ouvre la boîte
 * d'information, quand le legacy (`canCancel` faux) poussait `move-session` — sa branche « acceptée »
 * de `DialogM` n'était jamais atteinte. `cannotCancel` n'est pas un état de la séance : c'est la
 * réponse 400 de `cancelSubstitution`, que l'écran pose.
 */
export function tutorSessionNoticeFor(
    session: Pick<UITutorHomeSession, "lateMinutes" | "substitutionId" | "substitutionAccepted">
): Exclude<TutorSessionNotice, "cannotCancel"> | null {
    if (session.lateMinutes != null) return "late"
    if (session.substitutionId != null)
        return session.substitutionAccepted ? "substitutionAccepted" : "substitutionPending"
    return null
}
